// Pure helpers of the 1:1 compose drawer (spec 06 §4).
import { renderTemplate, type TemplateRow } from "../../domain/email";
import type { ComposeAttachment } from "../api/types";
import { AI_PLACEHOLDERS } from "../../../../../supabase/functions/_shared/ai/types.ts";

export const MAX_TOTAL_BYTES = 10 * 1024 * 1024; // GAVION send-fixes (was 3 MB)
export const COMPOSE_TEMPLATE_KINDS = [
  "one_to_one",
  "transactional",
  "sequence",
] as const;
const EMAIL_RE = /^[^@\s<>]+@[^@\s<>]+\.[^@\s<>]+$/;
const MB = 1024 * 1024;

export function parseRecipients(raw: string): {
  valid: string[];
  invalid: string[];
} {
  const parts = raw
    .split(/[\s,;]+/)
    .map((s) => s.trim().toLowerCase())
    .filter(Boolean);
  return {
    valid: [...new Set(parts.filter((p) => EMAIL_RE.test(p)))],
    invalid: parts.filter((p) => !EMAIL_RE.test(p)),
  };
}

/** Removes from `list` every address already present in `other` (Cc never repeats To). */
export const excludeRecipients = (
  list: readonly string[],
  other: readonly string[],
): string[] => list.filter((x) => !other.includes(x));

/** A finite numeric id, or null (guards `Number(...)` casts of record ids). */
export const toId = (v: unknown): number | null => {
  if (v === null || v === undefined || v === "") return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};

const BLOCKED_EXTENSIONS = new Set([
  "exe",
  "bat",
  "cmd",
  "com",
  "scr",
  "js",
  "vbs",
  "msi",
  "ps1",
  "jar",
]);
/** Executables and scripts are never attached. */
export const isBlockedFile = (name: string): boolean => {
  const dot = name.lastIndexOf(".");
  return dot > 0 && BLOCKED_EXTENSIONS.has(name.slice(dot + 1).toLowerCase());
};

export const totalSize = (a: readonly ComposeAttachment[]): number =>
  a.reduce((n, x) => n + x.size, 0);
/** GAVION template attachments (review I1): the send refuses more than 10 files. */
export const MAX_ATTACHMENTS = 10;
export const canAdd = (a: readonly ComposeAttachment[], size: number) =>
  a.length < MAX_ATTACHMENTS && totalSize(a) + size <= MAX_TOTAL_BYTES;
export const remainingBytes = (a: readonly ComposeAttachment[]): number =>
  Math.max(0, MAX_TOTAL_BYTES - totalSize(a));
/** "2,1 Mo" (fr) / "2.1 MB" (en), one decimal. */
export const formatMb = (bytes: number, locale: string): string => {
  const en = locale === "en";
  const n = new Intl.NumberFormat(en ? "en-CA" : "fr-CA", {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  }).format(bytes / MB);
  return `${n} ${en ? "MB" : "Mo"}`;
};

export function firstEmail(contact: {
  email_jsonb?: readonly { email?: string; type?: string }[] | null;
}): string[] {
  const e = contact.email_jsonb?.find(
    (x) => x.email && EMAIL_RE.test(x.email.trim()),
  )?.email;
  return e ? [e.trim().toLowerCase()] : [];
}

/** The address an empty « À » starts with: the contact's first valid email, else the company's. */
export function suggestedRecipients(
  contact: Parameters<typeof firstEmail>[0] | null,
  company: { email?: string | null } | null,
): string[] {
  const fromContact = contact ? firstEmail(contact) : [];
  if (fromContact.length > 0) return fromContact;
  return parseRecipients(company?.email ?? "").valid.slice(0, 1);
}

export function applyTemplate(
  t: TemplateRow,
  vars: Record<string, unknown>,
): { subject: string; html: string } {
  return {
    subject: renderTemplate(t.subject, vars, { escape: false }).output,
    html: renderTemplate(t.body_html, vars).output,
  };
}

// GAVION campaign-fixes: one signature rule with the sequence runner (supabase/functions/_shared/email/signature.ts).
export { withSignature } from "../../../../../supabase/functions/_shared/email/signature.ts";

/** Body with no visible content (empty paragraphs / line breaks only). */
export const isBlankHtml = (html: string): boolean =>
  html.replace(/<\/?p>|<br\s*\/?>|&nbsp;|\s/g, "") === "";

const visibleText = (html: string): string =>
  html.replace(/<[^>]*>/g, "").replace(/&nbsp;|\s/g, "");
/** The body holds text beyond the signature (a template would overwrite typed work). */
export const hasTypedText = (html: string, signature: string): boolean => {
  const body = visibleText(html);
  return body !== "" && body !== visibleText(signature);
};

/** A `[[…]]` drafting placeholder is still present: never send. */
export const hasPlaceholder = (...parts: string[]): boolean =>
  parts.some((p) => p.includes("[["));

type TemplateStatus = {
  draft?: boolean | null;
  archived?: boolean | null;
  approved_at?: string | null;
  subject: string;
  body_html: string;
};
/** Approved (non-draft, approved_at set), not archived, and free of placeholders. */
export const isUsableTemplate = (t: TemplateStatus): boolean =>
  t.draft === false &&
  t.archived !== true &&
  t.approved_at != null &&
  !hasPlaceholder(t.subject, t.body_html);

export function readFileAsAttachment(file: File): Promise<ComposeAttachment> {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onerror = () => reject(r.error);
    r.onload = () => {
      const s = String(r.result);
      resolve({
        name: file.name,
        contentType: file.type || "application/octet-stream",
        contentBase64: s.slice(s.indexOf(",") + 1),
        size: file.size,
      });
    };
    r.readAsDataURL(file);
  });
}

/** GAVION ai-email (spec §3): an AI « [à compléter] » left anywhere blocks the send until the rep replaces it. */
export const hasAiPlaceholder = (...parts: string[]): boolean =>
  parts.some((p) => AI_PLACEHOLDERS.some((x) => p.includes(x)));

/** GAVION ai-email: same visible text (the editor may re-serialise the HTML it was given). */
export const sameVisibleText = (a: string, b: string): boolean =>
  visibleText(a) === visibleText(b);
